import { useEffect } from "react";
import useSuperAdminStore from "@/context/superadmin/useSuperAdminStore";
import { useContracts } from "@/hooks/contracts";
import { useProducts } from "@/hooks/products";
import useEffectiveCustomerId from "@/hooks/useEffectiveCustomerId";
import { canAccessPortalPath, getDefaultPortalPage } from "@/router/routeAccess";
import { isLicensedProduct } from "@/utils/contractHelpers";
function getUniqueContractProductCodes(contracts) {
    if (!contracts) return [];
    return Array.from(new Set(contracts.map((contractDetails)=>contractDetails.contract.ProductCode).filter((productCode)=>!!productCode)));
}
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
function getMetadata(value) {
    if (!isRecord(value)) return undefined;
    const metadata = value.Metadata ?? value.metadata;
    return isRecord(metadata) ? metadata : undefined;
}
function isLicensedClusterMetadata(metadata) {
    const contractModel = metadata?.contractModel ?? metadata?.contract_model;
    if (typeof contractModel !== "string") return false;
    return contractModel.toUpperCase().replace(/[-\s]/g, "_") === "LICENSED_CLUSTER";
}
function isLicensedClusterContract(contractDetails) {
    if (contractDetails.contract.license_terms) return true;
    return isLicensedClusterMetadata(getMetadata(contractDetails.contract)) || isLicensedClusterMetadata(getMetadata(contractDetails.usagePlan));
}
function isLicensedClusterProductCode(productCode, contracts, products) {
    const product = products.find((item)=>item.ProductCode === productCode);
    if (isLicensedProduct(product) || isLicensedClusterMetadata(getMetadata(product))) {
        return true;
    }
    const matchingContracts = contracts.filter((contractDetails)=>contractDetails.contract.ProductCode === productCode);
    return matchingContracts.some(isLicensedClusterContract);
}
function hasOnlyLicensedClusterProducts(contracts, products) {
    const productCodes = getUniqueContractProductCodes(contracts);
    if (!contracts || productCodes.length === 0) return false;
    const availableProducts = products ?? [];
    return productCodes.every((productCode)=>isLicensedClusterProductCode(productCode, contracts, availableProducts));
}
export function usePortalNavigationAccess(enabled = true) {
    const { userRole, isSuperAdmin, selectedCustomerId, customerIdentifier, hasResolvedCustomerSelection } = useEffectiveCustomerId();
    const { setSelectedCustomer } = useSuperAdminStore();
    useEffect(()=>{
        if (enabled && isSuperAdmin && selectedCustomerId === undefined) {
            setSelectedCustomer(customerIdentifier);
        }
    }, [
        enabled,
        isSuperAdmin,
        selectedCustomerId,
        customerIdentifier,
        setSelectedCustomer
    ]);
    const canLoadContracts = enabled && !!userRole && hasResolvedCustomerSelection && (isSuperAdmin || !!customerIdentifier);
    const contractsQuery = useContracts(undefined, canLoadContracts);
    const contractProductCodes = getUniqueContractProductCodes(contractsQuery.data?.contracts);
    const shouldLoadProducts = canLoadContracts && contractProductCodes.length > 0;
    const productsQuery = useProducts(shouldLoadProducts);
    const isLoadingCustomerScope = enabled && !!userRole && !hasResolvedCustomerSelection;
    const isLoadingProducts = shouldLoadProducts && productsQuery.isLoading;
    const isLoading = isLoadingCustomerScope || canLoadContracts && contractsQuery.isLoading || isLoadingProducts;
    const isClusterOnlyCustomer = hasOnlyLicensedClusterProducts(contractsQuery.data?.contracts, productsQuery.data?.products);
    const defaultPage = getDefaultPortalPage(userRole, isClusterOnlyCustomer);
    return {
        canAccessPath: (pathname)=>canAccessPortalPath(pathname, isClusterOnlyCustomer),
        defaultPage,
        isClusterOnlyCustomer,
        isLoading
    };
}
